"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeEventProbabilitiesByMarket = void 0;
const flow_1 = __importDefault(require("lodash/fp/flow"));
const groupBy_1 = __importDefault(require("lodash/fp/groupBy"));
const flatMap_1 = __importDefault(require("lodash/fp/flatMap"));
const react_fast_compare_1 = __importDefault(require("react-fast-compare"));
const _reduxjs_1 = require("../../../@reduxjs");
const events_1 = require("../../../events");
const types_1 = require("../../../../containers/ConfigProvider/types");
const getEventProbabilityByMarketKey_1 = require("../../getEventProbabilityByMarketKey");
const isParameterValueMatching_1 = require("../isParameterValueMatching");
const getCategoryMarketInfoFilteredTradingStatus_1 = require("../getCategoryMarketInfoFilteredTradingStatus");
const constants_1 = require("../../constants");
const getEventProbabilityOutcomeName_1 = require("./getEventProbabilityOutcomeName");
const computeEventProbabilitiesByMarket = ({ eventId, sportId, eventStatus, marketCategoriesMap, marketsMap, playersMap, categoryMarketInfosMap, eventCompetitors, eventMarkets, eventProbabilitiesByMarketStore, marketCategoriesInfosByEventStore, clientType, }) => {
    // Set to track all new and updated keys in the eventProbabilitiesByMarketStore for the event
    const newEventProbabilityByMarketKeysSet = new Set();
    // Init the eventProbabilitiesByMarketStore for the event
    if (!eventProbabilitiesByMarketStore[eventId]) {
        eventProbabilitiesByMarketStore[eventId] = {};
    }
    const eventMarketCategoriesInfosMap = {};
    eventMarkets.forEach(({ marketId, probabilities }) => {
        // Step 1: Filter probabilities according to the trading status
        const tradingProbabilities = probabilities.filter((probability) => !constants_1.EVENT_PROBABILITY_EXCLUDE_TRADING_STATUSES.includes(probability.tradingStatus));
        if (!tradingProbabilities.length) {
            // Skip if no trading probabilities are present
            // eslint-disable-next-line no-useless-return
            return;
        }
        else {
            // Directly access the corresponding category infos using the marketId
            const categoryInfos = categoryMarketInfosMap[marketId] || [];
            // Step 2: Filter category market infos according to event sport
            // For safety reasons, TODO remove if passing tests without event sport consideration
            const categoryInfosOfEventSport = categoryInfos.filter(({ marketCategory }) => marketCategory.sportIds.includes(sportId));
            categoryInfosOfEventSport.forEach(({ categoryMarketInfo, marketCategory }) => {
                var _a;
                const marketCategoryId = marketCategory.id;
                const categoryMarketInfoId = categoryMarketInfo.id;
                // Step 3: Filter probabilities according to parameters matching
                // For safety reasons, TODO remove if passing tests without parameters matching consideration
                const matchingTradingProbabilities = tradingProbabilities.filter((probability) => categoryMarketInfo.parameters.every((categoryMarketInfoParameter) => probability.parameters.some((pParameter) => {
                    return (0, isParameterValueMatching_1.isParameterValueMatching)(pParameter, categoryMarketInfoParameter);
                })));
                if (!matchingTradingProbabilities.length) {
                    // eslint-disable-next-line no-useless-return
                    return;
                }
                else {
                    // Step 4: Enriching probabilities with player names – necessary for computing probabilities outcomes names
                    const matchingTradingProbabilitiesWithPlayerNames = (0, events_1.getEventProbabilitiesEnrichedWithPlayerNames)({
                        eventProbabilities: matchingTradingProbabilities,
                        playersMap,
                    });
                    // Step 5: Filtering & sorting probabilities according to market.orderedOutcomeTypes
                    const orderedOutcomeTypes = ((_a = marketsMap[marketId]) === null || _a === void 0 ? void 0 : _a.orderedOutcomeTypes) || [];
                    const sortedMatchingTradingProbabilitiesOutcomes = (0, flow_1.default)((0, groupBy_1.default)('outcomeTypeId'), (matchingTradingProbabilitiesMap) => (0, flatMap_1.default)((outcomeType) => (matchingTradingProbabilitiesMap[outcomeType.id] || []).map((eventProbability) => ({
                        ...eventProbability,
                        outcomeTypeData: {
                            ...outcomeType,
                            // Step 6: Computing probabilities outcomes names
                            name: (0, getEventProbabilityOutcomeName_1.getEventProbabilityOutcomeName)({
                                eventProbabilityOutcome: {
                                    ...eventProbability,
                                    outcomeTypeData: outcomeType,
                                },
                                eventCompetitors,
                                isShortNamed: getCategoryMarketInfoShortNamedParam({
                                    categoryMarketInfo,
                                    clientType,
                                }),
                                transformParamValueFunc: transformParamValue(categoryMarketInfo),
                            }),
                        },
                    })), orderedOutcomeTypes))(matchingTradingProbabilitiesWithPlayerNames);
                    // Step 7: Storing results
                    // Computing key
                    const key = (0, getEventProbabilityByMarketKey_1.getEventProbabilityByMarketKey)({
                        marketId,
                        categoryMarketInfoId,
                    });
                    // Add the key to the set of new keys
                    newEventProbabilityByMarketKeysSet.add(key);
                    // Computing value
                    const eventProbabilitiesByMarketStoreValue = {
                        eventId,
                        marketCategoryId,
                        marketId,
                        categoryMarketInfoId,
                        probabilities: sortedMatchingTradingProbabilitiesOutcomes,
                    };
                    // Skip affecting store, if no diff
                    if (!(0, react_fast_compare_1.default)(eventProbabilitiesByMarketStore[eventId][key], eventProbabilitiesByMarketStoreValue)) {
                        // Step 7.1: Updating eventProbabilitiesByMarketStore
                        eventProbabilitiesByMarketStore[eventId][key] =
                            eventProbabilitiesByMarketStoreValue;
                    }
                    // Step 7.2: Pre-computing marketCategoriesInfosByEventStore values
                    const eventMarketCategoriesInfosValue = {
                        ...categoryMarketInfo,
                        tradingStatus: (0, getCategoryMarketInfoFilteredTradingStatus_1.getCategoryMarketInfoFilteredTradingStatus)({
                            probabilities: sortedMatchingTradingProbabilitiesOutcomes,
                            eventStatus,
                        }),
                    };
                    if (!eventMarketCategoriesInfosMap[marketCategoryId]) {
                        eventMarketCategoriesInfosMap[marketCategoryId] = {
                            [categoryMarketInfoId]: eventMarketCategoriesInfosValue,
                        };
                    }
                    else {
                        eventMarketCategoriesInfosMap[marketCategoryId][categoryMarketInfoId] = eventMarketCategoriesInfosValue;
                    }
                }
            });
        }
    });
    // Step 7.3 Computing marketCategoriesInfosByEventStore values: sorting is important
    const marketCategoriesInfosByEventStoreValue = marketCategoriesMap.sortedIds
        .filter((marketCategoryId) => !!eventMarketCategoriesInfosMap[marketCategoryId])
        .map((marketCategoryId) => {
        var _a, _b;
        const categoryMarketInfos = (0, _reduxjs_1.getValues)(((_a = marketCategoriesMap.entries[marketCategoryId]) === null || _a === void 0 ? void 0 : _a.orderedMarketsIds) || [], eventMarketCategoriesInfosMap[marketCategoryId]).map((categoryMarketInfo) => ({
            ...categoryMarketInfo,
            title: categoryMarketInfo.title || '', // to exclude null in title from invalid data
        }));
        return {
            marketCategoryId,
            marketCategoryName: ((_b = marketCategoriesMap.entries[marketCategoryId]) === null || _b === void 0 ? void 0 : _b.name) || '',
            categoryMarketInfos,
        };
    });
    // Skip affecting store, if no diff
    if (!(0, react_fast_compare_1.default)(marketCategoriesInfosByEventStore[eventId], marketCategoriesInfosByEventStoreValue)) {
        // Step 7.4: Updating marketCategoriesInfosByEventStore
        marketCategoriesInfosByEventStore[eventId] =
            marketCategoriesInfosByEventStoreValue;
    }
    // Step 8: Identify and remove keys from eventProbabilitiesByMarketStore for the event that are not in the newEventProbabilityByMarketKeysSet
    Object.keys(eventProbabilitiesByMarketStore[eventId]).forEach((existingKey) => {
        if (!newEventProbabilityByMarketKeysSet.has(existingKey)) {
            delete eventProbabilitiesByMarketStore[eventId][existingKey];
        }
    });
};
exports.computeEventProbabilitiesByMarket = computeEventProbabilitiesByMarket;
// Function to transform parameter value for computing probability outcome name based on category market info
// TODO resolve is it the same for all core clients?
const transformParamValue = (categoryMarketInfo) => (value) => {
    let result = value;
    if (constants_1.TEMPLATES_WITH_PLUS_SIGN.includes(categoryMarketInfo.templateId)) {
        if (!value.startsWith('-') && Number(value) !== 0) {
            result = `+${result}`;
        }
    }
    if (constants_1.TEMPLATES_WITH_PARENTHESES.includes(categoryMarketInfo.templateId)) {
        result = `(${result})`;
    }
    return result;
};
// Helper function providing short name parameter value depending on a client type
const getCategoryMarketInfoShortNamedParam = ({ categoryMarketInfo, clientType, }) => [
    types_1.EClientTypes.WEB_MOBILE,
    types_1.EClientTypes.NATIVE_IOS,
    types_1.EClientTypes.NATIVE_ANDROID,
].includes(clientType)
    ? categoryMarketInfo.shortNamedMobile
    : categoryMarketInfo.shortNamed;
//# sourceMappingURL=computeEventProbabilitiesByMarket.js.map